import React from 'react';
import { Link } from 'react-router-dom';
import { CaretLeft, ScrollIcon, Receipt, ArrowCounterClockwise, Gavel, Warning, Handshake } from '@phosphor-icons/react';

/**
 * Public Terms & Conditions page for DefectR.
 *
 * Covers UK-standard digital-services terms: the Consumer Contracts
 * Regulations 2013 + Consumer Rights Act 2015. Importantly — because
 * DefectR delivers digital content IMMEDIATELY on purchase (property
 * unlock, lifetime tier) — the 14-day cooling-off right is waived by
 * the user at checkout (Reg. 37). That's explained plainly here.
 *
 * Kept intentionally short and scannable. If you need to update the
 * refund policy text, also reflect it in the Stripe product description
 * and in the pre-checkout upgrade modal copy (UpgradeContext.jsx).
 */
export default function Terms() {
    return (
        <div className="min-h-screen bg-white text-[#09090B]">
            <header className="border-b border-[#18181B] sticky top-0 bg-white z-30">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Link
                        to="/"
                        data-testid="terms-back-link"
                        className="p-2 -ml-2 hover:bg-[#F4F4F5] inline-flex items-center"
                        aria-label="Back to home"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </Link>
                    <img
                        src="/defectr-logo.png"
                        alt="DefectR"
                        className="h-8 w-auto object-contain"
                    />
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                        Terms &amp; Conditions
                    </span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10" data-testid="terms-page">
                <section>
                    <div className="flex items-center gap-3 mb-3">
                        <ScrollIcon size={24} weight="duotone" color="#1FA5CF" />
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                            Terms &amp; Conditions
                        </h1>
                    </div>
                    <p className="text-base text-[#52525B] leading-relaxed">
                        These terms are the contract between you and DefectR. They&apos;re written in plain
                        English because you shouldn&apos;t need a solicitor to understand what you&apos;re
                        paying for. UK consumer law applies in full.
                    </p>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-[#A1A1AA] mt-4">
                        Last updated: 5 May 2026 · Governed by the laws of England &amp; Wales
                    </p>
                </section>

                {/* 1. Who we are */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <h2 className="font-display text-xl font-semibold mb-3">1. Who we are</h2>
                    <p className="text-sm text-[#09090B] leading-relaxed">
                        &quot;DefectR&quot;, &quot;we&quot;, and &quot;us&quot; refers to <strong>DefectR Ltd</strong>,
                        a company registered in England &amp; Wales (company no.{' '}
                        <strong>09929002</strong>), with its registered office at{' '}
                        <strong>14 Ffordd Yr Odyn, Bwcle, Flintshire, CH7 2EZ</strong>. We operate the
                        DefectR app and the website <strong>defectr.co.uk</strong>. You can reach us at{' '}
                        <a href="mailto:support@defectr.co.uk" className="underline font-semibold">support@defectr.co.uk</a>.
                    </p>
                </section>

                {/* 2. The service */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <h2 className="font-display text-xl font-semibold mb-3">2. What DefectR does</h2>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>We provide a web app that lets you log defects in a new-build property, attach photos and floorplans, and share a report with your builder or housebuilder.</li>
                        <li>DefectR is a <strong>documentation tool</strong>. We are not a surveyor, a warranty provider, or a legal adviser. Anything AI-generated (severity, likely cause, estimated cost) is a guide, not a professional opinion.</li>
                        <li>We do not represent you in any dispute with your housebuilder, NHBC, or insurer. You own the report, and you decide how to use it.</li>
                    </ul>
                </section>

                {/* 3. Tiers & pricing */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Receipt size={18} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold">3. Tiers and pricing</h2>
                    </div>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li><strong>Foundation</strong> — free forever. One property, 10 defects, one floorplan, one PDF preview, three AI analyses.</li>
                        <li><strong>Rectify</strong> — <strong>£14.99 one-off</strong> per property. Unlocks unlimited defects, floorplans, share links, PDFs, builder replies and AI analysis for <em>that specific property</em>.</li>
                        <li><strong>Surveyor</strong> — <strong>£149.99 one-off</strong>, lifetime. Unlocks everything, on every property you ever create. No subscription.</li>
                        <li>All prices are in <strong>GBP</strong> and include any applicable UK VAT.</li>
                        <li>Payment is processed by <strong>Stripe</strong>. We never see your card number.</li>
                    </ul>
                </section>

                {/* 4. Refund policy — the important bit */}
                <section className="border-2 border-[#1FA5CF] bg-[#E8F5FB] p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <ArrowCounterClockwise size={20} weight="duotone" color="#1787AB" />
                        <h2 className="font-display text-xl font-semibold">4. Refund policy</h2>
                    </div>
                    <p className="text-sm text-[#09090B] leading-relaxed mb-3">
                        UK consumer law gives you a <strong>14-day cooling-off period</strong> on digital
                        purchases (Consumer Contracts Regulations 2013). Because DefectR&apos;s digital
                        content is delivered to you <strong>immediately</strong> at checkout — your property
                        unlock or Surveyor lifetime tier activates the second Stripe confirms the payment —
                        you expressly waive that 14-day right when you click <em>&quot;Continue to payment&quot;</em>.
                        This is standard for instant-download digital services.
                    </p>

                    <p className="text-sm text-[#09090B] leading-relaxed mb-3">
                        That said, we don&apos;t want anyone to feel trapped. We&apos;ll give you a{' '}
                        <strong>full refund, no questions asked</strong>, in any of the following cases:
                    </p>
                    <ul className="space-y-2 text-sm text-[#09090B] list-disc list-inside mb-3">
                        <li>The unlock failed to activate after payment (technical fault on our side).</li>
                        <li>You were charged more than once for the same property.</li>
                        <li>You paid for Rectify but the property was deleted or refunded by the builder within <strong>14 days of payment</strong>, and you have not generated a shared report or PDF export.</li>
                        <li>The app is materially unfit for purpose under the <strong>Consumer Rights Act 2015</strong> — i.e. it doesn&apos;t do what we said it would and we can&apos;t fix it.</li>
                    </ul>

                    <p className="text-sm text-[#09090B] leading-relaxed">
                        <strong>How to request a refund:</strong> email{' '}
                        <a href="mailto:support@defectr.co.uk" className="underline font-semibold">support@defectr.co.uk</a>{' '}
                        with the subject <code className="text-xs bg-white/60 px-1">REFUND</code>, the email
                        address on your DefectR account, and your Stripe receipt if you have it. We aim to
                        respond within <strong>2 working days</strong> and refund approved requests to the
                        original card within <strong>5–10 working days</strong>.
                    </p>
                    <p className="text-xs text-[#52525B] mt-3">
                        This refund policy sits on top of — not instead of — your statutory rights under UK consumer law.
                    </p>
                </section>

                {/* 5. Acceptable use */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Handshake size={18} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold">5. Acceptable use</h2>
                    </div>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>Use DefectR only for lawful snagging of residential properties you own, rent, or have permission to document.</li>
                        <li>Don&apos;t upload content that is defamatory, infringes someone else&apos;s rights, or contains images of identifiable people without their consent.</li>
                        <li>Don&apos;t attempt to break, probe, or overload the service. We rate-limit abusive traffic and may suspend accounts that repeatedly trip protections.</li>
                        <li>One account per person. Sharing accounts voids your Rectify / Surveyor entitlements and may result in suspension without refund.</li>
                    </ul>
                </section>

                {/* 6. Liability */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Warning size={18} weight="duotone" color="#F59E0B" />
                        <h2 className="font-display text-xl font-semibold">6. Liability</h2>
                    </div>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>We provide DefectR on an <strong>&quot;as-is&quot; basis</strong>. We take reasonable care to keep the service running but we can&apos;t guarantee uninterrupted availability.</li>
                        <li>Our total liability to you for any claim is capped at <strong>the total amount you&apos;ve paid DefectR in the 12 months before the claim</strong>, or £100 — whichever is higher.</li>
                        <li>We do not exclude or limit liability for death, personal injury caused by negligence, fraud, or anything else that cannot be excluded under UK law.</li>
                        <li>Nothing in this section affects your statutory rights as a consumer.</li>
                    </ul>
                </section>

                {/* 7. Changes, closure */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <h2 className="font-display text-xl font-semibold mb-3">7. Changes &amp; account closure</h2>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>We may update these terms to reflect new features or legal changes. We&apos;ll email active users if the change is material; your continued use after that constitutes acceptance.</li>
                        <li>You can close your account any time from <Link to="/app/account" className="underline font-semibold text-[#1787AB]">your account page</Link>. Paid entitlements (Rectify unlocks, Surveyor lifetime) are non-transferable and end with the account.</li>
                        <li>We may suspend or close accounts that breach these terms, with a refund of any <em>unused</em> Surveyor-tier payment pro-rated at our discretion.</li>
                    </ul>
                </section>

                {/* 8. Governing law */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Gavel size={18} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold">8. Governing law &amp; disputes</h2>
                    </div>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>These terms are governed by the <strong>laws of England and Wales</strong>.</li>
                        <li>If we can&apos;t resolve a dispute by email, you can bring a claim in the English courts. If you live in Scotland or Northern Ireland, you retain the right to bring proceedings in your local courts under UK consumer law.</li>
                        <li>For unresolved consumer disputes, you can also use the UK government&apos;s{' '}
                            <a href="https://www.citizensadvice.org.uk/consumer/" target="_blank" rel="noreferrer noopener" className="underline font-semibold">Citizens Advice consumer service</a>.</li>
                    </ul>
                </section>

                {/* Contact */}
                <section>
                    <h2 className="font-display text-xl font-semibold mb-3">Questions?</h2>
                    <p className="text-sm text-[#52525B]">
                        Email <a href="mailto:support@defectr.co.uk" className="underline font-semibold">support@defectr.co.uk</a>.
                        For data-protection specifically, please see our{' '}
                        <Link to="/privacy" className="underline font-semibold text-[#1787AB]">Privacy &amp; security</Link> page.
                    </p>
                </section>
            </main>

            <footer className="border-t border-[#18181B]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-[#52525B]">
                    <span>© DefectR Ltd · Co. No. 09929002</span>
                    <Link to="/" className="underline" data-testid="terms-footer-home-link">
                        Back to home
                    </Link>
                </div>
            </footer>
        </div>
    );
}
